import { io } from 'socket.io-client';
import config from '../config';

let socket = null;

const messageCallbacks = new Set();
const typingCallbacks = new Set();
const messageReadCallbacks = new Set();
const messageChatUpdateCallbacks = new Set();
const connectedCallbacks = new Set();

const connect = (token) => {
  if (!token) return;

  if (socket) {
    disconnect();
  }

  socket = io(config.API_BASE_URL, {
    path: '/socket.io',
    auth: { token },
    transports: ['websocket', 'polling']
  });

  socket.on('connect', () => {
    console.log('[ChatService] Connected to chat server');

    connectedCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error('[ChatService] Error in connected callback:', error);
      }
    });
  });

  socket.on('disconnect', () => {
    console.log('[ChatService] Disconnected from chat server');
  });

  socket.on('new-message', (data) => {
    console.log('[ChatService] New message received:', data);

    messageCallbacks.forEach((callback) =>
      callback({
        type: 'history',
        data: data.chatHistory,
        chatId: data.chatId,
      })
    );
  });

  socket.on('chat:update', (data) => {
    console.log('[ChatService] New message chat:update:', data);

    messageChatUpdateCallbacks.forEach((callback) => callback(data));
  });

  socket.on('messages-read', (data) => {
    console.log('[ChatService] Messages marked as read:', data);

    messageReadCallbacks.forEach((callback) => callback(data));
  });

  socket.on('typing:start', (data) => {
    console.log('[ChatService] Typing started:', data);

    typingCallbacks.forEach((callback) =>
      callback({
        ...data,
        isTyping: true
      })
    );
  });

  socket.on('typing:stop', (data) => {
    console.log('[ChatService] Typing stopped:', data);

    typingCallbacks.forEach((callback) =>
      callback({
        ...data,
        isTyping: false
      })
    );
  });

  socket.on('error', (error) => {
    console.error('[ChatService] Socket error:', error);
  });
};

const isConnected = () => {
  return socket?.connected || false;
};

const onConnected = (callback) => {
  connectedCallbacks.add(callback);

  // Если к моменту подписки сокет уже подключен,
  // вызываем callback сразу.
  if (socket?.connected) {
    callback();
  }

  return () => {
    connectedCallbacks.delete(callback);
  };
};

const disconnect = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

const joinChat = (chatId) => {
  if (!chatId) {
    return;
  }

  console.log('[ChatService] joinChat called', {
    chatId,
    socketExists: !!socket,
    connected: socket?.connected,
    socketId: socket?.id
  });

  if (socket?.connected) {
    socket.emit('join-chat', chatId);

    console.log('[ChatService] Joined chat:', chatId);

    return;
  }

  // Сокет еще не подключен.
  // Ждем connect и после этого входим в room.
  const unsubscribe = onConnected(() => {
    if (!socket?.connected) {
      return;
    }

    socket.emit('join-chat', chatId);

    console.log('[ChatService] Joined chat after socket connection:', chatId);

    unsubscribe();
  });
};

const leaveChat = (chatId) => {
  if (!chatId || !socket?.connected) return;

  socket.emit('leave-chat', String(chatId));
};

const sendMessage = (chatId, senderId, content) => {
  if (socket?.connected) {
    socket.emit('send-message', {
      chatId,
      senderId,
      content
    });
  }
};

const onMessageRead = (callback) => {
  messageReadCallbacks.add(callback);

  return () => messageReadCallbacks.delete(callback);
};

const markAsRead = (chatId, userId) => {
  if (socket?.connected) {
    console.log('[ChatService] mark-read:', {
      chatId,
      userId
    });

    socket.emit('mark-read', {
      chatId,
      userId
    });
  }
};

const onMessage = (callback) => {
  messageCallbacks.add(callback);

  return () => messageCallbacks.delete(callback);
};

const onChatUpdate = (callback) => {
  messageChatUpdateCallbacks.add(callback);

  return () => messageChatUpdateCallbacks.delete(callback);
};

const startTyping = (chatId, userId) => {
  if (socket?.connected) {
    socket.emit('typing:start', {
      chatId,
      userId
    });
  }
};

const stopTyping = (chatId, userId) => {
  if (socket?.connected) {
    socket.emit('typing:stop', {
      chatId,
      userId
    });
  }
};

const onTyping = (callback) => {
  typingCallbacks.add(callback);

  return () => typingCallbacks.delete(callback);
};

async function getChatToUserId(axiosPrivate, targetUserId) {
  try {
    return await axiosPrivate.post(config.API.CHATS.CREATE(targetUserId)).then((res) => res.data);
  } catch (error) {
    console.error('[ChatService] Error fetching to create chat:', error);
    throw error;
  }
}

async function deleteChatSoft(axiosPrivate, chatId) {
  try {
    return await axiosPrivate.delete(config.API.CHATS.DELETED(chatId)).then((res) => res.data);
  } catch (error) {
    console.error('[ChatService] Error fetching to deleted chat:', error);
    throw error;
  }
}

async function getUserChats(axiosPrivate, page = 1, limit = 20, q = '') {
  try {
    const response = await axiosPrivate.get(config.API.CHATS.BASE, {
      params: {
        page,
        limit,
        q
      },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }
    });

    if (!response.data || !response.data.data) {
      throw new Error('Invalid response from server');
    }

    return {
      status: 'success',
      data: {
        chats: response.data.data
      }
    };
  } catch (error) {
    console.error('[ChatService] Error fetching user chats:', error);
    throw error;
  }
}

async function getChatHistory(axiosPrivate, chatId, page = 1, limit = 200) {
  try {
    console.log('[ChatService] Fetching chat history for chatId:', chatId);

    const response = await axiosPrivate.get(config.API.CHATS.HISTORY(chatId), {
      params: {
        page,
        limit
      },
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }
    });

    if (!response.data) {
      throw new Error('Invalid response from server');
    }

    return {
      status: 'success',
      data: {
        chats: response.data.data
      }
    };
  } catch (error) {
    console.error('[ChatService] Error fetching chat history:', error);
    throw error;
  }
}

async function findChatUserByUserName(axiosPrivate, name) {
  try {
    const response = await axiosPrivate.get(config.API.CHATS.FIND_BY_USERNAME(name));

    return response.data.data;
  } catch (error) {
    console.error('[ChatService] Error fetching findChatUserByUsername:', error);

    throw error;
  }
}

const chatService = {
  connect,
  disconnect,
  joinChat,
  leaveChat,  
  getChatToUserId,
  sendMessage,
  onMessageRead,
  markAsRead,
  onMessage,
  onTyping,
  getUserChats,
  getChatHistory,
  deleteChatSoft,
  findChatUserByUserName,
  startTyping,
  stopTyping,
  isConnected,
  onChatUpdate,
  onConnected
};

export default chatService;
